import { useState, type FormEvent } from "react";

import { Alert, Button, Card, Input, Label } from "@realroof/ui";

import { useSession } from "../session";

/** Staff login: password first, then the OTP sent to the staff member's own mobile. */
export function Login() {
  const { sendOtp, verify, ended } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(e: FormEvent, step: () => Promise<void>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await step();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-sidebar p-4">
      <Card className="w-full max-w-[400px] p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <span aria-hidden="true" className="grid size-8 place-items-center rounded-md bg-primary text-base text-primary-foreground">
            R
          </span>
          RealRoof Admin
        </h1>
        {ended && !sentTo && <Alert tone="warning">Your session ended. Log in again.</Alert>}
        {!sentTo ? (
          <form onSubmit={(e) => run(e, async () => setSentTo(await sendOtp(email, password)))} className="flex flex-col gap-3.5">
            <Label>
              Email
              <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Label>
            <Label>
              Password
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Label>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "Checking…" : "Continue"}
            </Button>
          </form>
        ) : (
          <form onSubmit={(e) => run(e, () => verify(email, code))} className="flex flex-col gap-3.5">
            <p>We sent a 6-digit code to your mobile {sentTo}.</p>
            <Label>
              Code
              <Input className="font-mono text-lg tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </Label>
            <Button type="submit" variant="primary" disabled={busy || code.length !== 6}>
              {busy ? "Checking…" : "Log in"}
            </Button>
            <Button variant="link" className="self-start" onClick={() => (setSentTo(null), setCode(""))}>
              Back
            </Button>
          </form>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-muted-foreground">Staff only. Every action here is recorded.</p>
      </Card>
    </main>
  );
}
